import { Company, RightEnum } from "@finway-group/shared/lib/models"
import { notification } from "antd"
import React, { Suspense, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { Redirect, Switch } from "react-router-dom"

import FullScreenLoader from "Components/FullScreenLoader"
import Loading from "Components/loading"
import ScrollToTop from "Components/scrollToTop"
import { lazily } from "Shared/utils/react.utils"
import { ConfirmEmail, ForgotPassword, Login, NewPasswordPage, ResetPasswordPage } from "Features/authentication"
import AuthErrorPage from "Features/authentication/AuthErrorPage"
import ErrorPage from "Features/errors/ErrorPage"
import NotFoundPage from "Features/errors/NotFoundPage"
import LiquidityContainer from "Features/pages/liquidity/liquidity.container"
import { isIntercomEnabled } from "Shared/config/consts"
import { SelectedInboxInvoicesProvider } from "Shared/context/selectedExpenses.context"
import { useApiVersion } from "Shared/hooks/apiVersion.hooks"
import { useAuth } from "Shared/hooks/auth.hooks"
import { useCompanies } from "Shared/hooks/company.hooks"
import { useIsFreeVersion } from "Shared/hooks/featureFlags.hooks"
import { useLoggedInEmployeeProfile } from "Shared/hooks/user.hooks"
import { AuthzService } from "Shared/services"

import { ErrorRoute } from "./ErrorRoute"
import { FullScreenRoute, PrivateFullScreen } from "./FullScreenRoute"
import { PrivateRoute } from "./PrivateRoute"
import { RoutePathEnum } from "./RoutePath.enum"

// lazy loading
const { SettingsPage } = lazily(() => import("Features/settings/SettingsPage"))
const Intercom = React.lazy(() => import("Components/intercom"))

const { Dashboard } = lazily(() => import("Features/pages/dashboard/dashboard"))
const ExpenseDetails = React.lazy(() => import("Features/pages/expenses/expenseDetails/expenseDetails.container"))

const { CostCentersList } = lazily(() => import("Features/pages/costCenters/CostCentersList"))
const CostCenterDetail = React.lazy(() => import("Features/pages/costCenters/costCenterDetails/costCenterDetails.container"))

const { EmployeesPage } = lazily(() => import("Features/pages/employees/EmployeesPage"))
const EmployeeDetail = React.lazy(() => import("Features/pages/employees/employeeDetails.container"))

const { VendorsList } = lazily(() => import("Features/pages/vendors/VendorsList"))
const VendorDetail = React.lazy(() => import("Features/pages/vendors/vendor/vendorDetails.container"))

const { SubscriptionsList } = lazily(() => import("Features/pages/subscriptions/SubscriptionsList"))
const SubscriptionDetails = React.lazy(() => import("Features/pages/subscriptions/subscriptionDetails.container"))

const { CreditCardsContainer } = lazily(() => import("Features/pages/creditCards/CreditCardsContainer"))
const CreditCardDetail = React.lazy(() => import("Features/pages/creditCards/creditCardContainer/creditCardDetails.container"))
const WalletDetailsContainer = React.lazy(() => import("Features/pages/creditCards/walletDetails/walletDetails.container"))

const { TransactionsList } = lazily(() => import("Features/pages/transactions/TransactionsList"))
const TransactionsDetails = React.lazy(() => import("Features/pages/transactions/transactionsDetails.container"))

const { WorkflowsList } = lazily(() => import("Features/pages/workflows/WorkflowsList"))

const Reports = React.lazy(() => import("Features/pages/reporting/Reports.container"))

const { PurchaseOrdersListPage } = lazily(() => import("Features/pages/purchaseOrders/PurchaseOrdersListPage"))
const { PurchaseOrderDetailsPage } = lazily(() => import("Features/pages/purchaseOrders/PurchaseOrdersDetailsPage"))

const { DeliveryNotesListPage } = lazily(() => import("Features/pages/deliveryNotes/DeliveryNotesListPage"))

const { DeliveryNoteDetailsPage } = lazily(() => import("Features/pages/deliveryNotes/DeliveryNoteDetailsPage"))

export const AppRouter = () => {
    const { t } = useTranslation()
    const { isAuthenticated, logout, companyId, featureFlags } = useAuth()

    const companies = useCompanies()
    const cardsEnabled = companies.find((company: Company) => company._id === companyId)?.cardsEnabled

    const isFreeVersion = useIsFreeVersion()
    const loggedInEmployee = useLoggedInEmployeeProfile()

    const { checkedVersion } = useApiVersion()

    useEffect(() => {
        if (isAuthenticated && companyId) {
            // Make sure to update the token by logging loggedInEmployee out if any feature flag is missing
            if (featureFlags && (featureFlags.paidVersion === undefined || featureFlags.stripeEnabled === undefined || featureFlags.outgoingWireTransferEnabled === undefined)) {
                notification.open({
                    type: "info",
                    message: t("notification:new_api_version.title"),
                    description: t("notification:new_api_version.message"),
                    duration: 0,
                })
                logout()
            }

            loggedInEmployee.setActiveCompanyProfile(companyId)
        }
    }, [loggedInEmployee, companyId])

    return !checkedVersion ? (
        <FullScreenLoader />
    ) : (
        <Suspense fallback={<Loading />}>
            {isIntercomEnabled && (
                <Suspense fallback={<Loading />}>
                    <Intercom />
                </Suspense>
            )}
            <ScrollToTop />

            <Switch>
                <FullScreenRoute exact path={RoutePathEnum.LOGIN}>
                    <Login />
                </FullScreenRoute>
                <FullScreenRoute path={RoutePathEnum.FORGOT_PASSWORD}>
                    <ForgotPassword />
                </FullScreenRoute>
                <PrivateFullScreen path={RoutePathEnum.NEW_PASSWORD}>
                    <NewPasswordPage />
                </PrivateFullScreen>
                <FullScreenRoute path={RoutePathEnum.RESET_TOKEN}>
                    <ResetPasswordPage />
                </FullScreenRoute>
                <FullScreenRoute path={RoutePathEnum.CONFIRM_EMAIL}>
                    <ConfirmEmail />
                </FullScreenRoute>

                {/* // TODO: @John and @Vittorio re-add */}
                {/* <FullScreenRoute path="/deforce-sso/:code">
                        <DeforceSso />
                    </FullScreenRoute> */}

                <PrivateRoute path={RoutePathEnum.SETTINGS} includedFreeVersion>
                    <Suspense fallback={<Loading />}>
                        <SettingsPage />
                    </Suspense>
                </PrivateRoute>

                <PrivateRoute exact path="/" includedFreeVersion>
                    <Redirect to={{ pathname: isFreeVersion ? RoutePathEnum.LM : RoutePathEnum.EXPENSES }} />
                </PrivateRoute>

                <PrivateRoute exact path={RoutePathEnum.EXPENSES}>
                    <Suspense fallback={<Loading />}>
                        <Dashboard />
                    </Suspense>
                </PrivateRoute>

                <PrivateRoute exact path={RoutePathEnum.EXPENSE_DETAIL_PAGE}>
                    <SelectedInboxInvoicesProvider>
                        <Suspense fallback={<Loading />}>
                            <ExpenseDetails />
                        </Suspense>
                    </SelectedInboxInvoicesProvider>
                </PrivateRoute>

                <PrivateRoute exact path={RoutePathEnum.FOLDER_EXPENSE_DETAIL_PAGE}>
                    <SelectedInboxInvoicesProvider>
                        <ExpenseDetails />
                    </SelectedInboxInvoicesProvider>
                </PrivateRoute>

                <PrivateRoute exact path={RoutePathEnum.SUBSCRIPTIONS}>
                    <Suspense fallback={<Loading />}>
                        <SubscriptionsList />
                    </Suspense>
                </PrivateRoute>

                <PrivateRoute exact path={RoutePathEnum.SUBSCRIPTION_DETAIL_PAGE}>
                    <Suspense fallback={<Loading />}>
                        <SubscriptionDetails />
                    </Suspense>
                </PrivateRoute>

                {/* MIND THE ORDER! This needs to be BEFORE CreditCardDetail */}
                <PrivateRoute exact path={RoutePathEnum.WALLET_DETAIL_PAGE} canSee={cardsEnabled && AuthzService.isRightGrantedForLoggedInUser(RightEnum.CARD_WALLET__ALL__READ)}>
                    <Suspense fallback={<Loading />}>
                        <WalletDetailsContainer />
                    </Suspense>
                </PrivateRoute>

                <PrivateRoute exact path={RoutePathEnum.CARDS} canSee={cardsEnabled}>
                    <Suspense fallback={<Loading />}>
                        <CreditCardsContainer />
                    </Suspense>
                </PrivateRoute>

                <PrivateRoute exact path={RoutePathEnum.CARD_DETAIL_PAGE} canSee={cardsEnabled}>
                    <Suspense fallback={<Loading />}>
                        <CreditCardDetail />
                    </Suspense>
                </PrivateRoute>

                <PrivateRoute exact path={RoutePathEnum.TRANSACTIONS} includedFreeVersion canSee={AuthzService.isRightGrantedForLoggedInUser(RightEnum.TRANSACTION__ALL__READ)}>
                    <Suspense fallback={<Loading />}>
                        <TransactionsList />
                    </Suspense>
                </PrivateRoute>

                <PrivateRoute exact path={RoutePathEnum.TRANSACTION_DETAIL_PAGE} includedFreeVersion>
                    <Suspense fallback={<Loading />}>
                        <TransactionsDetails />
                    </Suspense>
                </PrivateRoute>

                <PrivateRoute exact path={RoutePathEnum.WORKFLOWS} canSee={AuthzService.isRightGrantedForLoggedInUser(RightEnum.WORKFLOW__ALL__READ)}>
                    <Suspense fallback={<Loading />}>
                        <WorkflowsList />
                    </Suspense>
                </PrivateRoute>

                <PrivateRoute exact path={RoutePathEnum.WORKFLOW_DETAIL_PAGE} canSee={AuthzService.isRightGrantedForLoggedInUser(RightEnum.WORKFLOW__ALL__READ)}>
                    <Suspense fallback={<Loading />}>
                        <WorkflowsList />
                    </Suspense>
                </PrivateRoute>

                <PrivateRoute exact path={RoutePathEnum.EMPLOYEES} includedFreeVersion>
                    <Suspense fallback={<Loading />}>
                        <EmployeesPage />
                    </Suspense>
                </PrivateRoute>

                <PrivateRoute exact path={RoutePathEnum.EMPLOYEE_DETAIL_PAGE} includedFreeVersion>
                    <Suspense fallback={<Loading />}>
                        <EmployeeDetail />
                    </Suspense>
                </PrivateRoute>

                <PrivateRoute exact path={RoutePathEnum.VENDORS} canSee={AuthzService.isRightGrantedForLoggedInUser(RightEnum.VENDOR__ALL__READ)}>
                    <Suspense fallback={<Loading />}>
                        <VendorsList />
                    </Suspense>
                </PrivateRoute>
                <PrivateRoute exact path={RoutePathEnum.VENDOR_DETAIL_PAGE} canSee={AuthzService.isRightGrantedForLoggedInUser(RightEnum.VENDOR__ALL__READ)}>
                    <Suspense fallback={<Loading />}>
                        <VendorDetail />
                    </Suspense>
                </PrivateRoute>

                <PrivateRoute exact path={RoutePathEnum.COST_CENTERS}>
                    <Suspense fallback={<Loading />}>
                        <CostCentersList />
                    </Suspense>
                </PrivateRoute>

                <PrivateRoute exact path={RoutePathEnum.COST_CENTER_DETAIL_PAGE}>
                    <Suspense fallback={<Loading />}>
                        <CostCenterDetail />
                    </Suspense>
                </PrivateRoute>

                <PrivateRoute path={RoutePathEnum.LM} includedFreeVersion canSee={AuthzService.isRightGrantedForLoggedInUser(RightEnum.LIQUIDITY__ALL__READ)}>
                    <LiquidityContainer />
                </PrivateRoute>

                <PrivateRoute exact path={RoutePathEnum.REPORTS} canSee={AuthzService.isRightGrantedForLoggedInUser(RightEnum.REPORTING__ALL__READ)}>
                    <Suspense fallback={<Loading />}>
                        <Reports />
                    </Suspense>
                </PrivateRoute>

                <PrivateRoute exact path={RoutePathEnum.PURCHASE_ORDERS}>
                    <Suspense fallback={<Loading />}>
                        <PurchaseOrdersListPage />
                    </Suspense>
                </PrivateRoute>

                <PrivateRoute exact path={RoutePathEnum.PURCHASE_ORDER_DETAILS_PAGE}>
                    <Suspense fallback={<Loading />}>
                        <PurchaseOrderDetailsPage />
                    </Suspense>
                </PrivateRoute>

                <PrivateRoute exact path={RoutePathEnum.DELIVERY_NOTES}>
                    <Suspense fallback={<Loading />}>
                        <DeliveryNotesListPage />
                    </Suspense>
                </PrivateRoute>

                <PrivateRoute exact path={RoutePathEnum.DELIVERY_NOTE_DETAILS_PAGE}>
                    <Suspense fallback={<Loading />}>
                        <DeliveryNoteDetailsPage />
                    </Suspense>
                </PrivateRoute>

                <ErrorRoute path={RoutePathEnum.ERROR}>
                    <ErrorPage />
                </ErrorRoute>
                <FullScreenRoute path={RoutePathEnum.AUTH_ERROR}>
                    <AuthErrorPage />
                </FullScreenRoute>

                {/* this will redirect any URL without a route to "/not-found" */}
                <ErrorRoute path={RoutePathEnum.GLOBAL}>
                    <NotFoundPage />
                </ErrorRoute>
            </Switch>
        </Suspense>
    )
}
